import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Home, Inbox, CalendarCheck, Users, Clock, Search, Sparkles, Bell, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUSES = ["new", "in-progress", "closed"];
const statusColor = {
  new: "text-cyan-400 border-cyan-400/40",
  "in-progress": "text-amber-300 border-amber-300/40",
  closed: "text-white/40 border-white/20",
};

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [reminderHours, setReminderHours] = useState(24);
  const [stats, setStats] = useState({ leads: 0, new_leads: 0, bookings: 0, clients: 0, waitlist: 0 });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const match = (obj) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return ["name", "email", "company", "service", "message", "status", "date"]
      .some((k) => (obj[k] || "").toString().toLowerCase().includes(q));
  };
  const filteredLeads = leads.filter(match);
  const filteredBookings = bookings.filter(match);

  // Topic demand aggregation for the waitlist tab
  const topicDemand = Object.entries(
    waitlist.reduce((acc, w) => {
      (w.topics || []).forEach((t) => { acc[t] = (acc[t] || 0) + 1; });
      return acc;
    }, {})
  ).sort((a, b) => b[1] - a[1]);
  const maxDemand = topicDemand.length ? topicDemand[0][1] : 1;

  const load = async () => {
    try {
      const [l, b, s, w, cfg] = await Promise.all([
        api.get("/admin/leads"),
        api.get("/admin/bookings"),
        api.get("/admin/stats"),
        api.get("/admin/waitlist"),
        api.get("/admin/settings"),
      ]);
      setLeads(l.data);
      setBookings(b.data);
      setStats(s.data);
      setWaitlist(w.data);
      setReminderHours(cfg.data.reminder_hours_before);
    } catch {
      toast.error("Failed to load admin data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveReminderHours = async () => {
    try {
      const { data } = await api.patch("/admin/settings", { reminder_hours_before: Number(reminderHours) });
      setReminderHours(data.reminder_hours_before);
      toast.success(`Reminders will send ${data.reminder_hours_before}h before each consultation.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed.");
    }
  };

  const exportWaitlist = async () => {
    try {
      const resp = await api.get("/admin/waitlist/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "aisho_waitlist.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Waitlist exported.");
    } catch {
      toast.error("Export failed.");
    }
  };

  const updateStatus = async (lead_id, status) => {
    try {
      await api.patch(`/admin/leads/${lead_id}`, { status });
      setLeads((ls) => ls.map((l) => (l.lead_id === lead_id ? { ...l, status } : l)));
      toast.success(`Marked ${status}.`);
    } catch {
      toast.error("Update failed.");
    }
  };

  const [editing, setEditing] = useState(null); // booking_id being rescheduled
  const [resched, setResched] = useState({ date: "", time: "" });

  const updateBooking = async (booking_id, payload) => {
    try {
      await api.patch(`/admin/bookings/${booking_id}`, payload);
      setBookings((bs) => bs.map((b) => (b.booking_id === booking_id ? { ...b, ...payload } : b)));
      setEditing(null);
      toast.success(`Booking ${payload.status}. Client notified by email.`);
    } catch {
      toast.error("Update failed.");
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#050505] grain">
      <header className="border-b border-white/10 bg-[#050505]/85 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="font-serif text-2xl text-white">Ai<span className="text-cyan-400">.</span>Sho</Link>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400 border border-cyan-400/40 px-2 py-1 rounded-full">Admin</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 hover:text-cyan-400 transition-colors"><Home size={14} /> Site</Link>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 hover:text-cyan-400 transition-colors"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 md:px-10 py-16">
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="font-serif text-5xl md:text-6xl text-white leading-none mb-10">
          Command Center.
        </motion.h1>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden mb-14">
          {[
            { icon: Inbox, n: stats.leads, l: "Total Leads" },
            { icon: Clock, n: stats.new_leads, l: "New Leads" },
            { icon: CalendarCheck, n: stats.bookings, l: "Bookings" },
            { icon: Users, n: stats.clients, l: "Clients" },
            { icon: Sparkles, n: stats.waitlist, l: "Waitlist" },
          ].map((s) => (
            <div key={s.l} className="bg-[#0C0C0C] p-6">
              <s.icon size={20} strokeWidth={1.25} className="text-cyan-400 mb-4" />
              <p className="font-serif text-4xl text-white">{s.n}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">{s.l}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-white/10 mb-8">
          <div className="flex gap-6">
            {["leads", "bookings", "waitlist"].map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
                className={`pb-4 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${tab === t ? "text-cyan-400 border-b-2 border-cyan-400" : "text-white/40 hover:text-white"}`}>
                {t}
              </button>
            ))}
          </div>
          <div className="relative pb-4 sm:pb-0">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 pb-0 sm:top-[45%]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, service…"
              data-testid="admin-search"
              className="w-full sm:w-72 bg-[#0C0C0C] border border-white/15 rounded-full h-10 pl-9 pr-4 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-400/60 transition-colors"
            />
          </div>
        </div>

        {tab === "bookings" && (
          <div className="flex flex-wrap items-center gap-3 mb-8 bg-[#0C0C0C] border border-white/10 rounded-sm p-4" data-testid="reminder-settings">
            <Bell size={15} className="text-cyan-400" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Send reminder</span>
            <input type="number" min="1" max="168" value={reminderHours}
              onChange={(e) => setReminderHours(e.target.value)} data-testid="reminder-hours-input"
              className="w-20 bg-[#050505] border border-white/15 rounded-sm h-9 px-3 text-sm text-white text-center focus:outline-none focus:border-cyan-400/60" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">hours before each confirmed consultation</span>
            <button onClick={saveReminderHours} data-testid="reminder-save"
              className="h-9 px-4 rounded-full bg-cyan-400 text-black font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-white transition-colors">Save</button>
          </div>
        )}

        {loading ? (
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/40 animate-pulse">Loading…</p>
        ) : tab === "leads" ? (
          filteredLeads.length === 0 ? <p className="text-white/40" data-testid="admin-leads-empty">{leads.length === 0 ? "No leads yet." : "No leads match your search."}</p> : (
            <div className="space-y-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
              {filteredLeads.map((l) => (
                <div key={l.lead_id} className="bg-[#0C0C0C] p-6 md:p-7" data-testid={`admin-lead-${l.lead_id}`}>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <p className="text-white font-medium">{l.name}</p>
                        <span className="font-mono text-[11px] text-white/40">{l.email}</span>
                        {l.company && <span className="font-mono text-[11px] text-white/30">· {l.company}</span>}
                      </div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400 mb-2">{l.service || "General"}</p>
                      <p className="text-white/70 text-sm leading-relaxed max-w-2xl">{l.message}</p>
                      <p className="font-mono text-[10px] text-white/25 mt-3">{new Date(l.created_at).toLocaleString()}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {STATUSES.map((s) => (
                        <button key={s} onClick={() => updateStatus(l.lead_id, s)} data-testid={`admin-lead-${l.lead_id}-${s}`}
                          className={`font-mono text-[10px] uppercase tracking-[0.15em] border px-2.5 py-1.5 rounded-full transition-colors ${l.status === s ? statusColor[s] : "text-white/30 border-white/10 hover:border-white/30"}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : tab === "bookings" ? (
          bookings.length === 0 ? <p className="text-white/40">No bookings yet.</p> : filteredBookings.length === 0 ? <p className="text-white/40" data-testid="admin-bookings-empty">No bookings match your search.</p> : (
            <div className="space-y-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
              {filteredBookings.map((b) => {
                const bColor = { requested: "text-cyan-400 border-cyan-400/40", confirmed: "text-emerald-300 border-emerald-300/40", rescheduled: "text-amber-300 border-amber-300/40", cancelled: "text-red-400 border-red-400/40" }[b.status] || "text-white/40 border-white/20";
                return (
                  <div key={b.booking_id} className="bg-[#0C0C0C] p-6 md:p-7" data-testid={`admin-booking-${b.booking_id}`}>
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3">
                          <p className="text-white font-medium">{b.name}</p>
                          <span className="font-mono text-[11px] text-white/40">{b.email}</span>
                          <span className={`font-mono text-[9px] uppercase tracking-[0.15em] border px-2 py-0.5 rounded-full ${bColor}`}>{b.status}</span>
                        </div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400 mt-2">{b.service || "General"}</p>
                        {b.message && <p className="text-white/60 text-sm mt-2 max-w-2xl">{b.message}</p>}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-serif text-2xl text-white">{b.date}</p>
                        <p className="font-mono text-[11px] text-cyan-400">{b.time}</p>
                      </div>
                    </div>

                    <div className="mt-5 flex flex-wrap items-center gap-2">
                      <button onClick={() => updateBooking(b.booking_id, { status: "confirmed" })} data-testid={`admin-booking-${b.booking_id}-confirm`}
                        className="font-mono text-[10px] uppercase tracking-[0.15em] border border-emerald-300/40 text-emerald-300 hover:bg-emerald-300 hover:text-black px-3 py-1.5 rounded-full transition-colors">Confirm</button>
                      <button onClick={() => { setEditing(editing === b.booking_id ? null : b.booking_id); setResched({ date: b.date, time: b.time }); }} data-testid={`admin-booking-${b.booking_id}-reschedule`}
                        className="font-mono text-[10px] uppercase tracking-[0.15em] border border-amber-300/40 text-amber-300 hover:bg-amber-300 hover:text-black px-3 py-1.5 rounded-full transition-colors">Reschedule</button>
                      <button onClick={() => updateBooking(b.booking_id, { status: "cancelled" })} data-testid={`admin-booking-${b.booking_id}-cancel`}
                        className="font-mono text-[10px] uppercase tracking-[0.15em] border border-red-400/40 text-red-400 hover:bg-red-400 hover:text-black px-3 py-1.5 rounded-full transition-colors">Cancel</button>
                    </div>

                    {editing === b.booking_id && (
                      <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-white/10 pt-4">
                        <div>
                          <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 mb-1">New date</label>
                          <input type="date" value={resched.date} onChange={(e) => setResched({ ...resched, date: e.target.value })} className="bg-[#050505] border border-white/15 rounded-sm h-9 px-2 text-sm text-white [color-scheme:dark]" />
                        </div>
                        <div>
                          <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 mb-1">New time</label>
                          <input type="time" value={resched.time} onChange={(e) => setResched({ ...resched, time: e.target.value })} className="bg-[#050505] border border-white/15 rounded-sm h-9 px-2 text-sm text-white [color-scheme:dark]" />
                        </div>
                        <button onClick={() => updateBooking(b.booking_id, { status: "rescheduled", date: resched.date, time: resched.time })} data-testid={`admin-booking-${b.booking_id}-save`}
                          className="h-9 px-4 rounded-full bg-cyan-400 text-black font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-white transition-colors">Save &amp; Notify</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        ) : (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <p className="text-white/50 text-sm">{waitlist.length} signup{waitlist.length === 1 ? "" : "s"} · demand grouped by topic</p>
              <button onClick={exportWaitlist} disabled={waitlist.length === 0} data-testid="waitlist-export"
                className="flex items-center gap-2 bg-cyan-400 hover:bg-white disabled:opacity-30 text-black rounded-full h-10 px-5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors">
                <Download size={14} /> Export CSV
              </button>
            </div>

            {topicDemand.length === 0 ? (
              <p className="text-white/40" data-testid="admin-waitlist-empty">No waitlist signups yet.</p>
            ) : (
              <>
                <div className="space-y-3 mb-12">
                  {topicDemand.map(([topic, count]) => (
                    <div key={topic} className="flex items-center gap-4" data-testid={`waitlist-demand-${topic}`}>
                      <span className="w-48 shrink-0 text-sm text-white/80">{topic}</span>
                      <div className="flex-1 h-3 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-cyan-400 rounded-full transition-all" style={{ width: `${(count / maxDemand) * 100}%` }} />
                      </div>
                      <span className="w-8 text-right font-mono text-sm text-cyan-400">{count}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
                  {waitlist.map((w) => (
                    <div key={w.email} className="bg-[#0C0C0C] p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-2" data-testid={`waitlist-row-${w.email}`}>
                      <div>
                        <p className="text-white">{w.name || w.email}</p>
                        <p className="font-mono text-[11px] text-white/40">{w.email}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(w.topics || []).map((t) => (
                          <span key={t} className="font-mono text-[9px] uppercase tracking-[0.15em] border border-cyan-400/30 text-cyan-400/80 px-2 py-1 rounded-full">{t}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
